import setAutoRunAddinCommandWaitingQueue from '../actions/setAutoRunAddinCommandWaitingQueue';
import getExtensibilityState from '../store/getExtensibilityState';
import IAutoRunAddinCommand from '../store/schema/interfaces/IAutoRunAddinCommand';
import destroyAutoRunAddinCommandFromWaitingQueue from '../actions/destroyAutoRunAddinCommandFromWaitingQueue';
import IAddinCommand from '../store/schema/interfaces/IAddinCommand';
import setUilessExtendedAddinCommand from '../actions/setUilessExtendedAddinCommand';
import AutoRunAddinCommand from '../store/schema/AutoRunAddinCommand';
import InvokeAppAddinCommandStatusCode from '../store/schema/enums/InvokeAppAddinCommandStatusCode';
import isAddinCommandBlocked from './isAddinCommandBlocked';
import isHostItemActive from './isHostItemActive';
import setBlockedAddinCommand from '../actions/setBlockedAddinCommand';
import IExtendedAddinCommand from '../store/schema/interfaces/IExtendedAddinCommand';
import IAutoRunAddinCommandTelemetry from '../store/schema/interfaces/IAutoRunAddinCommandTelemetry';
import { logUsage } from 'owa-analytics';
import { getScenarioFromHostItemIndex } from 'owa-addins-store';
import { getApp } from 'owa-config';
import { ObservableMap } from 'mobx';

// PER_HOSTITEMINDEX_MAX_LIMIT: Number of autorun add-ins allowed to execute simultaneously per hostItemIndex
export const PER_HOSTITEMINDEX_MAX_LIMIT = 5;
// GLOBAL_MAX_LIMIT: Number of autorun add-ins allowed to execute simultaneously across all hostItemIndex
export const GLOBAL_MAX_LIMIT = 20;
// MAX_TRIES: Number of retries an autorun add-in is executed in case of failure
const MAX_TRIES = 3;

/*
    @addAutoRunAddinsToWaitingQueue
    Accepts a list of AutoRunAddinCommands and calls the setAutoRunAddinCommandWaitingQueue mutator for each of the commands
*/
export const addAutoRunAddinsToWaitingQueue = (
    extendedAutoRunAddinCommands: IExtendedAddinCommand[],
    hostItemIndex: string
) => {
    extendedAutoRunAddinCommands.forEach((extendedAddinCommand: IExtendedAddinCommand) => {
        const autoRunAddinCommand = extendedAddinCommand.addinCommand as IAutoRunAddinCommand;
        if (
            !isAddinCommandBlocked(hostItemIndex, autoRunAddinCommand) &&
            canAddAutoRunAddinCommandToQueue(autoRunAddinCommand, hostItemIndex)
        ) {
            (extendedAddinCommand.addinCommandTelemetry as IAutoRunAddinCommandTelemetry).setQueuePushTime();
            setAutoRunAddinCommandWaitingQueue(extendedAddinCommand, hostItemIndex);
        }
    });
};

/*
    @processAutoRunWaitingQueue
    Reads the AutoRunAddinCommandWaitingQueue and adds the correct autorun addinCommand to the runningUILessExtendedAddinCommands
*/
export const processAutoRunWaitingQueue = (hostItemIndex: string) => {
    const autoRunExtededAddinCommands = getAutoRunAddinsFromWaitingQueue(hostItemIndex);

    autoRunExtededAddinCommands.forEach((extendedAddinCommand: IExtendedAddinCommand) => {
        const addinCommand = extendedAddinCommand.addinCommand as IAutoRunAddinCommand;
        const addinId: string = addinCommand.get_Id();
        if (
            !isAddinCommandBlocked(hostItemIndex, addinCommand) &&
            canExecuteAddinForId(addinId, hostItemIndex)
        ) {
            (extendedAddinCommand.addinCommandTelemetry as IAutoRunAddinCommandTelemetry).setQueuePopTime();
            executeAutoRunAddin(extendedAddinCommand, hostItemIndex);
            destroyAutoRunAddinCommandFromWaitingQueue(hostItemIndex, addinCommand);
        }
    });
};

/*
    @executeAutoRunAddin
    Executes the autorun addinCommand command and checks if the addinCommand executed successfully
*/
export const executeAutoRunAddin = async (
    extendedAddinCommand: IExtendedAddinCommand,
    hostItemIndex: string
) => {
    const autorunAddinCommand = extendedAddinCommand.addinCommand as IAutoRunAddinCommand;
    for (let retryCount = 0; retryCount < MAX_TRIES; retryCount++) {
        const controlId = extendedAddinCommand.controlId;
        const result = await setUilessExtendedAddinCommand(
            controlId,
            extendedAddinCommand,
            hostItemIndex
        );
        if (
            result.status === InvokeAppAddinCommandStatusCode.Success ||
            !isHostItemActive(hostItemIndex)
        ) {
            return;
        }

        if (retryCount == MAX_TRIES - 1) {
            logUsage('AddinExecutionBlocked', {
                id: autorunAddinCommand.extension.Id,
                controlId: controlId,
                owa_1: getApp(),
                owa_2: getScenarioFromHostItemIndex(hostItemIndex),
            });
        }
    }
    setBlockedAddinCommand(autorunAddinCommand, hostItemIndex);
    destroyAutoRunAddinCommandFromWaitingQueue(hostItemIndex, autorunAddinCommand);
    processAutoRunWaitingQueue(hostItemIndex);
};

/*
    @getAutoRunAddinsFromWaitingQueue
    Returns the IAutoRunAddinCommand list from the extendedAddinWaitingQueue for the given hostItemIndex
*/
export const getAutoRunAddinsFromWaitingQueue = (
    hostItemIndex: string
): IExtendedAddinCommand[] => {
    const autoRunAddinWaitingQueue = getExtensibilityState().autoRunAddinCommandWaitingQueue.get(
        hostItemIndex
    );
    return autoRunAddinWaitingQueue ? autoRunAddinWaitingQueue : [];
};

/*
    @canExecuteAddinForId
    Checks if the addin can be executed for the current hostItemIndex
*/
export const canExecuteAddinForId = (addinId: string, hostItemIndex: string): boolean => {
    if (isAutoRunAddinExecutingForAddinId(addinId, hostItemIndex)) {
        return false;
    }
    if (!!isMaxAddinLimitExceeded(hostItemIndex) || !!isGlobalMaxAddinLimitExceeded()) {
        return false;
    }
    return true;
};

/*
    @isAutoRunAddinExecutingForAddinId
    Checks if the addin is executing for the given hostItemIndex
*/
export const isAutoRunAddinExecutingForAddinId = (
    addinId: string,
    hostItemIndex: string
): boolean => {
    const runningUILessExtendedAddinCommands = getExtensibilityState()
        .runningUILessExtendedAddinCommands;
    let isAddinExecuting: boolean = false;

    if (!runningUILessExtendedAddinCommands.has(hostItemIndex)) {
        return false;
    }

    runningUILessExtendedAddinCommands
        .get(hostItemIndex)
        .forEach((extendedAddinCommand: IExtendedAddinCommand) => {
            const addinCommand = extendedAddinCommand.addinCommand;
            if (isAutoRunAddinCommand(addinCommand) && addinCommand.get_Id() === addinId) {
                isAddinExecuting = true;
            }
        });
    return isAddinExecuting;
};

/*
    @isGlobalMaxAddinLimitExceeded
    Checks if the total number of executing autorun addins have exceeded GLOBAL_MAX_LIMIT
*/
export const isGlobalMaxAddinLimitExceeded = (): boolean => {
    const runningUILessExtendedAddinCommands = getExtensibilityState()
        .runningUILessExtendedAddinCommands;
    let globalAddinCount: number = 0;

    runningUILessExtendedAddinCommands.forEach(
        (uiLessAddins: ObservableMap<string, IExtendedAddinCommand>) => {
            uiLessAddins.forEach((extendedAddinCommand: IExtendedAddinCommand) => {
                const addinCommand = extendedAddinCommand.addinCommand;
                if (isAutoRunAddinCommand(addinCommand)) {
                    globalAddinCount += 1;
                }
            });
        }
    );

    return globalAddinCount >= GLOBAL_MAX_LIMIT;
};

/*
    @isMaxAddinLimitExceeded
    Checks if the total number of executing autorun addins for given hostItemIndex have exceeded PER_HOSTITEMINDEX_MAX_LIMIT
*/
export const isMaxAddinLimitExceeded = (hostItemIndex: string): boolean => {
    const runningUILessExtendedAddinCommands = getExtensibilityState()
        .runningUILessExtendedAddinCommands;
    let addinCount: number = 0;

    if (runningUILessExtendedAddinCommands.has(hostItemIndex)) {
        runningUILessExtendedAddinCommands
            .get(hostItemIndex)
            .forEach((extendedAddinCommand: IExtendedAddinCommand) => {
                const addinCommand = extendedAddinCommand.addinCommand;
                if (isAutoRunAddinCommand(addinCommand)) {
                    addinCount += 1;
                }
            });
    }
    return addinCount >= PER_HOSTITEMINDEX_MAX_LIMIT;
};

/*
    @canAddAutoRunAddinCommandToQueue
    Checks if waiting queue already has an auto run addin waiting for execution for the same event
*/
export const canAddAutoRunAddinCommandToQueue = (
    addinCommand: IAutoRunAddinCommand,
    hostItemIndex: string
): boolean => {
    const autoRunExtendedAddinCommands: IExtendedAddinCommand[] = getAutoRunAddinsFromWaitingQueue(
        hostItemIndex
    );

    for (let iter = 0; iter < autoRunExtendedAddinCommands.length; iter++) {
        const autoRunAddinCommand = autoRunExtendedAddinCommands[iter]
            .addinCommand as IAutoRunAddinCommand;
        const isEventCommon: boolean =
            addinCommand.getLaunchEventType() === autoRunAddinCommand.getLaunchEventType();
        const isAddinIdCommon: boolean = addinCommand.get_Id() === autoRunAddinCommand.get_Id();

        if (isEventCommon && isAddinIdCommon) {
            return false;
        }
    }
    return true;
};

/*
    @isAutoRunAddinCommand
    Checks if the AddinCommand is an instance of AutoRunAddinCommand
*/
export const isAutoRunAddinCommand = (addinCommand: IAddinCommand): boolean => {
    return addinCommand instanceof AutoRunAddinCommand;
};
